// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import equipmentApi from './equipment.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchEquipmentApi = createAsyncThunk(
    'heroSaga/equipment/fetchEquipmentApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await equipmentApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleEquipmentPublishedStateApi = createAsyncThunk(
    'heroSaga/equipment/toggleEquipmentPublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createEquipmentApi = createAsyncThunk(
    'heroSaga/equipment/createEquipmentApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await equipmentApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeEquipmentApi = createAsyncThunk(
    'heroSaga/equipment/storeEquipmentApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const hero = await equipmentApi.store(values);
            alertSwal('success', 'Equipment created successfully!');

            return hero;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showEquipmentApi = createAsyncThunk(
    'heroSaga/equipment/showEquipmentApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await equipmentApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateEquipmentApi = createAsyncThunk(
    'heroSaga/equipment/updateEquipmentApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values } = params;

        try {
            dispatch(requestStarted());
            await equipmentApi.update(id, values);
            alertSwal('success', 'Equipment updated successfully!');

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteEquipmentApi = createAsyncThunk(
    'heroSaga/equipment/deleteEquipmentApi',
    async (params, { dispatch }) => {
        const { navigate, id } = params;

        try {
            await equipmentApi.delete(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const equipmentSlice = createSlice({
    name: 'heroSaga/equipment',
    initialState: {
        rows: [],
        model: {},
        editedModel: {},
        pictureAttributeNames: [],
        equipmentCategories: [],
        equipmentDurabilities: [],
    }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedEquipment(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEquipmentApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchEquipmentApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleEquipmentPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.isReady = !updateRow.isReady;
            })
            .addCase(toggleEquipmentPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createEquipmentApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
                state.equipmentCategories = payload.equipmentCategories;
                state.equipmentDurabilities = payload.equipmentDurabilities;
            })
            .addCase(createEquipmentApi.rejected, () => {
                //
            });

        builder
            .addCase(storeEquipmentApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeEquipmentApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showEquipmentApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
                state.equipmentCategories = payload.equipmentCategories;
                state.equipmentDurabilities = payload.equipmentDurabilities;
            })
            .addCase(showEquipmentApi.rejected, () => {
                //
            });

        builder
            .addCase(updateEquipmentApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.
                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);

                updatedRow.name = payload.name;
                updatedRow.categories = payload.categories;
                updatedRow.durabilities = state.equipmentDurabilities.filter((item) => {
                    return payload.durabilities.includes(item.id);
                });
            })
            .addCase(updateEquipmentApi.rejected, () => {
                //
            });

        builder.addCase(deleteEquipmentApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedEquipment } = equipmentSlice.actions;
